import {
  QueryResult,
  StoreObject,
  gql,
  useApolloClient,
  useMutation,
  useQuery,
} from '@apollo/client';
import { useEmployee, useLoginInput } from 'hooks';
import { EpEmployee } from 'types/graphql-types';

type PersonalData = {
  employee: Pick<
    EpEmployee,
    | 'firstName'
    | 'lastName'
    | 'qualification'
    | 'languages'
    | 'contractFromDate'
    | 'contractToDate'
    | 'department'
    | 'country'
    | 'city'
    | 'linkedIn'
    | 'photo'
  >;
};

const GET_PERSONAL_INFO = gql`
  query GetPersonalInfo($login: String!) {
    employee(login: $login) {
      firstName
      lastName
      qualification
      languages {
        name
        level
      }
      contractFromDate
      contractToDate
      department
      country
      city
      linkedIn
      photo
    }
  }
`;

const GET_COUNTRY_LIST = gql`
  query GetCountryList {
    countryList
  }
`;

const SAVE_PERSONAL_INFO = gql`
  mutation SavePersonalInfo($personalInfo: PersonalInfoInput!) {
    savePersonalInfo(personalInfo: $personalInfo) {
      login
      linkedIn
    }
  }
`;

type SavePersonalInfoReturnType = {
  savePersonalInfo: {
    login: string;
    linkedIn: string;
  };
};

export const useSavePersonalInfo = (): ((
  personalInfo: PersonalInfo,
) => Promise<void>) => {
  const loginInput = useLoginInput();
  const client = useApolloClient();
  const { employee } = useEmployee();
  const [savePersonalInfo] = useMutation(SAVE_PERSONAL_INFO, {
    onCompleted(data: SavePersonalInfoReturnType) {
      client.cache.modify({
        fields: {
          linkedIn() {
            return data.savePersonalInfo.linkedIn;
          },
        },
        id: client.cache.identify(employee as unknown as StoreObject),
      });
    },
  });

  return async (personalInfo) => {
    const opt = {
      variables: { personalInfo: { ...loginInput, ...personalInfo } },
    };

    await savePersonalInfo(opt);
  };
};

export const usePersonalInfo = (): QueryResult<PersonalData> =>
  useQuery<PersonalData>(GET_PERSONAL_INFO, { variables: useLoginInput() });

export const useCountryList = (): QueryResult<CountryListData> =>
  useQuery<CountryListData>(GET_COUNTRY_LIST);

type CountryListData = {
  countryList: string[];
};

export type PersonalInfo = {
  linkedIn?: string;
  city?: string;
  country?: string;
};
